import operationService from "@/services/operations/operationService";
import operationLocalDbService from "@/services/operations/operationLocalDbService";
import mapKeys from "lodash/mapKeys";

const INITIAL_STATE = {
  operations: [
    //   {
    //       "id": "string",
    //       "sort_order": 1,
    //       "service": "string",
    //       "download_available": true,
    //   },
  ],
};

export default {
  isRegistered: false,
  namespaced: true,
  state: {
    ...INITIAL_STATE,
  },
  mutations: {
    /**
     * operation設定をセットするミューテーション
     *
     * @param {Object} state - Vuex ストアの状態オブジェクト
     * @param {Operation[]} data - データ
     * @returns {void}
     */
    SET_OPERATIONS(state, data) {
      state.operations = data;
    },

    RESET_STATE(state) {
      Object.assign(state, INITIAL_STATE);
    },
  },
  actions: {
    /**
     * operation設定 を storeにセットする
     *
     * @param {Object} context - The Vuex store context object
     * @param {Function} context.commit - The commit function
     * @returns {Promise<void>}
     * @throws {Error}
     */
    async setOperationConfigurations({ commit }) {
      const operations = await operationService.getOperationConfigurations();
      commit("SET_OPERATIONS", operations.data || []);
      operationLocalDbService.setOperationConfigurationsToLocalDB(
        operations.data || []
      );
    },

    /**
     * operation設定 を localDB から取得し storeにセットする
     *
     * @param {Object} context - The Vuex store context object
     * @param {Function} context.commit - The commit function
     * @returns {Promise<void>}
     * @throws {Error}
     */
    async setOperationConfigurationsFromLocalDB({ commit }) {
      const operations = await operationLocalDbService.getOperationConfigurationsFromLocalDB();
      commit("SET_OPERATIONS", operations || []);
    },

    /**
     * operation設定 を localDB から取得し storeにセットする
     * localDBにない場合はAPIから取得し store にセットする
     *
     * @returns {Promise<void>}
     * @throws {Error}
     */
    async setOperationConfigurationsFromLocalDBAndAPI({ dispatch, getters }) {
      await dispatch("setOperationConfigurationsFromLocalDB");
      if (!getters.hasOperation) {
        dispatch("setOperationConfigurations");
      }
    },

    /**
     * APIでオペレーションに関係するマスタデータを取得
     *
     * @param {Object} context - The Vuex store context object
     * @param {Function} context.commit - The commit function
     * @returns {Promise<true>}
     * @throws {Error}
     */
    async setOperationCommonConfiguration({ dispatch }) {
      await Promise.all([
        dispatch(
          "operationAccounts/setOperationAccounts",
          { is_responsible: true },
          { root: true }
        ),
        dispatch("operationColumns/setOperationColumns", null, { root: true }),
        dispatch("setOperationConfigurations"),
        dispatch("operationVessels/setOperationVessels", null, { root: true }),
        dispatch("operationWorkflows/setOperationWorkflows", null, {
          root: true,
        }),
        dispatch("enterpriseWorkflows/setEnterpriseWorkflows", null, {
          root: true,
        }),
        dispatch("enterpriseForms/setEnterpriseForms", null, { root: true }),
      ]);
      return true;
    },

    /**
     * APIでオペレーションに関係するマスタデータを取得
     * localDBからデータ取得し、データがない場合はAPIから取得する
     *
     * @param {Object} context - The Vuex store context object
     * @param {Function} context.commit - The commit function
     * @returns {Promise<true>}
     * @throws {Error}
     */
    async setOperationCommonConfigurationFromLocalDBAndAPI({ dispatch }) {
      await Promise.all([
        dispatch(
          "operationAccounts/setOperationAccountsFromLocalDBAndAPI",
          null,
          { root: true }
        ),
        dispatch(
          "operationColumns/setOperationColumnsFromLocalDBAndAPI",
          null,
          { root: true }
        ),
        dispatch("setOperationConfigurationsFromLocalDBAndAPI"),
        dispatch(
          "operationVessels/setOperationVesselsFromLocalDBAndAPI",
          null,
          { root: true }
        ),
        dispatch(
          "operationWorkflows/setOperationWorkflowsFromLocalDBAndAPI",
          null,
          { root: true }
        ),
        dispatch(
          "enterpriseWorkflows/setEnterpriseWorkflowsFromLocalDBAndAPI",
          null,
          { root: true }
        ),
        dispatch("enterpriseForms/setEnterpriseFormsFromLocalDBAndAPI", null, {
          root: true,
        }),
      ]);
      return true;
    },

    /**
     * storeを初期値に戻す
     *
     * @param {Object} context - The Vuex store context object
     * @param {Function} context.commit - The commit function
     * @returns {Promise<void>}
     */
    async resetState({ commit }) {
      commit("RESET_STATE");
    },

    /**
     * operation機能に関するstoreを初期値に戻す
     *
     * @param {Object} context - The Vuex store context object
     * @param {Function} context.dispatch - The commit function
     * @returns {Promise<void>}
     */
    async resetOperationFeatureState({ dispatch }) {
      dispatch("resetOperationCommonConfiguration"); // マスタ系をリセット
      dispatch("operationReports/resetState", null, { root: true }); // レポートの一覧データをリセット
      dispatch("operationReport/resetState", null, { root: true }); // 単一のレポートのデータをリセット
    },

    /**
     * operation機能に関するマスタのstoreを初期値に戻す
     *
     * @param {Object} context - The Vuex store context object
     * @param {Function} context.dispatch - The commit function
     * @returns {Promise<void>}
     */
    async resetOperationCommonConfiguration({ dispatch }) {
      dispatch("operationAccounts/resetState", null, {
        root: true,
      });
      dispatch("operationColumns/resetState", null, { root: true });
      dispatch("resetState");
      dispatch("operationVessels/resetState", null, { root: true });
      dispatch("operationWorkflows/resetState", null, {
        root: true,
      });
      dispatch("enterpriseWorkflows/resetState", null, {
        root: true,
      });
      dispatch("enterpriseForms/resetState", null, { root: true });
    },
  },
  getters: {
    sortedValidOperations: state => {
      return operationService.sortedValidOperations(state.operations);
    },

    operationMap: (_, { sortedValidOperations }) => {
      if (!Array.isArray(sortedValidOperations)) return {};
      return mapKeys(sortedValidOperations, "id");
    },

    hasOperation: (_, { sortedValidOperations }) => {
      return sortedValidOperations.length > 0;
    },
  },
};
